import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Inbox, Trash2, Mail, Phone, Calendar as CalendarIcon, Users as UsersIcon, RefreshCw, Download, Search, X, BadgeCheck, Repeat, UserPlus, Copy, ArrowRightLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import LeadsStatsPanel from './LeadsStatsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-coral/15 text-coral border-coral/30' },
  { value: 'contacted', label: 'Contacted', color: 'bg-ocean/15 text-ocean border-ocean/30' },
  { value: 'quoted', label: 'Quoted', color: 'bg-terracotta/15 text-terracotta border-terracotta/30' },
  { value: 'booked', label: 'Booked', color: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' },
  { value: 'declined', label: 'Declined', color: 'bg-maroon/10 text-maroon/60 border-maroon/20' },
];

const statusMeta = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

// "1st", "2nd", "3rd", "4th"… for the repeat-customer badge
const ordinal = (n) => {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
};

// CSV helpers
const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).replace(/\r\n|\r|\n/g, ' ').trim();
  return /[",;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const downloadCSV = (rows, filename) => {
  const headers = ['Created', 'Name', 'Email', 'Phone', 'Package', 'Supplier', 'Travel Dates', 'Party Size', 'Status', 'Notes'];
  const body = rows.map((l) => [
    new Date(l.created_at).toISOString(),
    l.name,
    l.email,
    l.phone || '',
    l.package_title || '',
    l.supplier || '',
    l.travel_dates || '',
    l.party_size || '',
    l.status,
    l.notes || '',
  ].map(csvEscape).join(','));
  const csv = [headers.join(','), ...body].join('\r\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const LeadsManager = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/leads/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data);
    } catch {
      // Stats are non-essential — silently swallow so the table still loads.
    }
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (search.trim()) params.q = search.trim();
      const res = await axios.get(`${API}/leads`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLeads(res.data || []);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch on filter / date changes; search is applied client-side for instant feedback
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeads(); }, [filter, startDate, endDate]);

  // Stats are unfiltered totals — fetched once on mount and after any data-mutation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStats(); }, []);

  const updateStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/leads/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((ls) => ls.map((l) => (l.id === id ? res.data : l)));
      toast.success(`Marked as ${status}`);
      fetchStats();
    } catch {
      toast.error('Failed to update');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this lead permanently?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/leads/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success('Deleted');
      fetchStats();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const sendInvite = async (lead) => {
    if (lead.invite_sent_at && !window.confirm(`An invite was already sent on ${new Date(lead.invite_sent_at).toLocaleDateString()}. Resend a fresh email?`)) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/admin/leads/${lead.id}/invite`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, invite_sent_at: new Date().toISOString() } : l)));
      try { await navigator.clipboard.writeText(res.data.invite_url); } catch { /* noop */ }
      toast.success('Invite emailed — link also copied to clipboard');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send invite';
      toast.error(msg);
    }
  };

  const counts = useMemo(() => (
    STATUS_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: leads.filter((l) => l.status === o.value).length }), {})
  ), [leads]);

  // Apply free-text search client-side too (so it filters live without re-querying)
  const visible = useMemo(() => {
    if (!search.trim()) return leads;
    const q = search.trim().toLowerCase();
    return leads.filter((l) =>
      [l.name, l.email, l.package_title, l.supplier].some((v) => (v || '').toLowerCase().includes(q))
    );
  }, [leads, search]);

  const exportCsv = () => {
    if (!visible.length) {
      toast.info('Nothing to export with the current filters.');
      return;
    }
    const stamp = new Date().toISOString().slice(0, 10);
    const parts = ['chimba-leads', stamp];
    if (filter !== 'all') parts.push(filter);
    if (startDate || endDate) parts.push(`${startDate || 'any'}_to_${endDate || 'any'}`);
    downloadCSV(visible, `${parts.join('-')}.csv`);
    toast.success(`Exported ${visible.length} lead${visible.length === 1 ? '' : 's'}`);
  };

  const clearFilters = () => {
    setFilter('all');
    setStartDate('');
    setEndDate('');
    setSearch('');
  };

  const filterActive = filter !== 'all' || startDate || endDate || search;

  return (
    <div data-testid="leads-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-heading font-bold text-maroon flex items-center gap-2">
            <Inbox className="text-terracotta" size={28} />
            Quote Leads
          </h2>
          <p className="text-maroon/60 text-sm mt-1">Customer quote requests from Partner Packages with booking_type = quote_request</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={fetchLeads} variant="outline" className="border-maroon/30 text-maroon hover:bg-maroon/5 inline-flex items-center gap-2">
            <RefreshCw size={16} /> Refresh
          </Button>
          <Button onClick={exportCsv} className="btn-primary inline-flex items-center gap-2" data-testid="export-leads-csv-btn">
            <Download size={16} /> Export CSV
          </Button>
        </div>
      </div>

      {/* Stats panel — totals, conversion rate, 30-day sparkline */}
      <LeadsStatsPanel stats={stats} />

      {/* Filters */}
      <div className="bg-sand/30 border border-maroon/10 rounded-sm p-4 mb-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-maroon mb-1 block">Start date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              max={endDate || undefined}
              className="w-full px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
              data-testid="leads-start-date"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-maroon mb-1 block">End date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || undefined}
              className="w-full px-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
              data-testid="leads-end-date"
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-2 relative">
            <label className="text-xs font-semibold text-maroon mb-1 block">Search</label>
            <Search size={14} className="absolute left-3 top-[34px] text-maroon/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email, package or supplier"
              className="w-full pl-9 pr-3 py-2 bg-white border-2 border-maroon/20 rounded-sm text-maroon focus:border-maroon focus:outline-none"
              data-testid="leads-search"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <button
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 rounded-sm text-sm font-semibold border ${filter === 'all' ? 'bg-maroon text-cream border-maroon' : 'bg-white text-maroon border-maroon/20 hover:bg-maroon/5'}`}
            data-testid="leads-filter-all"
          >
            All ({leads.length})
          </button>
          {STATUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setFilter(o.value)}
              className={`px-3 py-1.5 rounded-sm text-sm font-semibold border ${filter === o.value ? 'bg-maroon text-cream border-maroon' : 'bg-white text-maroon border-maroon/20 hover:bg-maroon/5'}`}
              data-testid={`leads-filter-${o.value}`}
            >
              {o.label} ({counts[o.value] || 0})
            </button>
          ))}

          {filterActive ? (
            <button
              onClick={clearFilters}
              className="ml-auto text-xs text-maroon/60 hover:text-maroon underline inline-flex items-center gap-1"
              data-testid="leads-clear-filters"
            >
              <X size={12} /> Clear filters
            </button>
          ) : null}
        </div>
      </div>

      {loading ? (
        <p className="text-maroon/60">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="card text-center py-12">
          <Inbox size={48} className="mx-auto text-maroon/20 mb-3" />
          <p className="text-maroon/60">
            {filterActive ? 'No leads match your filters.' : "No leads yet — they'll appear here when customers submit a quote request."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {visible.map((lead) => {
            const meta = statusMeta(lead.status);
            return (
              <div key={lead.id} className="card" data-testid={`lead-row-${lead.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <h3 className="font-heading font-bold text-lg text-maroon truncate">
                        {lead.name}
                      </h3>
                      <span className={`px-2 py-0.5 rounded-sm text-xs font-bold border ${meta.color}`}>
                        {meta.label}
                      </span>
                      {lead.user_id && (
                        <Link
                          to={`/admin/customers/${lead.user_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold border bg-emerald-500/15 text-emerald-700 border-emerald-500/30 hover:bg-emerald-500/25 transition-colors"
                          data-testid={`lead-member-badge-${lead.id}`}
                          title="Open customer profile"
                        >
                          <BadgeCheck size={12} /> Member
                        </Link>
                      )}
                      {lead.user_id && (lead.member_lead_count > 1 || lead.member_booking_count > 0) && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold border bg-terracotta/15 text-terracotta border-terracotta/30"
                          data-testid={`lead-repeat-badge-${lead.id}`}
                          title={`${lead.member_lead_count || 0} quote request(s) · ${lead.member_booking_count || 0} booking(s) on file`}
                        >
                          <Repeat size={12} />
                          {lead.member_lead_index && lead.member_lead_count > 1
                            ? `${ordinal(lead.member_lead_index)} request`
                            : 'Repeat customer'}
                          {lead.member_booking_count > 0 && (
                            <span className="opacity-70">
                              {' · '}
                              {lead.member_booking_count} booking{lead.member_booking_count === 1 ? '' : 's'}
                            </span>
                          )}
                        </span>
                      )}
                      {!lead.user_id && lead.invite_sent_at && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold border bg-blue-500/10 text-blue-700 border-blue-500/30"
                          data-testid={`lead-invited-badge-${lead.id}`}
                          title={`Invite emailed ${new Date(lead.invite_sent_at).toLocaleString()}`}
                        >
                          <Copy size={11} /> Invited
                        </span>
                      )}
                      {lead.consultation_id && (
                        <Link
                          to={`/admin/consultations?focus=${lead.consultation_id}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold border bg-ocean/10 text-ocean border-ocean/30 hover:bg-ocean/20 transition-colors"
                          data-testid={`lead-from-consultation-${lead.id}`}
                          title="Originated from a consultation request"
                        >
                          <ArrowRightLeft size={11} /> From consultation
                        </Link>
                      )}
                    </div>
                    {lead.package_title && (
                      <p className="text-sm text-maroon/70">
                        <strong>Package:</strong> {lead.package_title}
                        {lead.supplier && <span className="text-maroon/50"> · {lead.supplier}</span>}
                      </p>
                    )}
                  </div>
                  <p className="text-xs text-maroon/40">
                    {new Date(lead.created_at).toLocaleString()}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-maroon/80 mb-3">
                  <div className="flex items-center gap-2">
                    <Mail size={14} className="text-maroon/40" />
                    <a href={`mailto:${lead.email}`} className="hover:text-terracotta">{lead.email}</a>
                  </div>
                  {lead.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-maroon/40" />
                      <a href={`tel:${lead.phone}`} className="hover:text-terracotta">{lead.phone}</a>
                    </div>
                  )}
                  {lead.travel_dates && (
                    <div className="flex items-center gap-2">
                      <CalendarIcon size={14} className="text-maroon/40" />
                      {lead.travel_dates}
                    </div>
                  )}
                  {lead.party_size && (
                    <div className="flex items-center gap-2">
                      <UsersIcon size={14} className="text-maroon/40" />
                      {lead.party_size}
                    </div>
                  )}
                </div>

                {lead.notes && (
                  <p className="text-sm text-maroon/70 bg-sand/30 border border-maroon/10 rounded-sm p-3 mb-3 whitespace-pre-line">
                    {lead.notes}
                  </p>
                )}

                <div className="flex items-center justify-between gap-2 pt-3 border-t border-maroon/10 flex-wrap">
                  <select
                    value={lead.status}
                    onChange={(e) => updateStatus(lead.id, e.target.value)}
                    className="px-3 py-1.5 bg-white border-2 border-maroon/20 rounded-sm text-maroon text-sm focus:border-maroon focus:outline-none"
                    data-testid={`lead-status-${lead.id}`}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2 flex-wrap">
                    {!lead.user_id && (
                      <button
                        onClick={() => sendInvite(lead)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-sm font-semibold border-2 border-terracotta/30 bg-terracotta/5 text-terracotta hover:bg-terracotta/10"
                        title={lead.invite_sent_at ? `Last sent ${new Date(lead.invite_sent_at).toLocaleDateString()} — click to resend` : 'Email a "create your account" link to this customer'}
                        data-testid={`lead-invite-${lead.id}`}
                      >
                        <UserPlus size={14} />
                        {lead.invite_sent_at ? 'Resend invite' : 'Invite to join'}
                      </button>
                    )}
                    <button
                      onClick={() => remove(lead.id)}
                      className="text-coral hover:bg-coral/10 px-3 py-1.5 rounded-sm text-sm font-semibold inline-flex items-center gap-1"
                      data-testid={`lead-delete-${lead.id}`}
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LeadsManager;
